import { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as CheckSvg } from "@/assets/images/check.svg";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/libs/utils";
import { useState } from "react";

interface ManufacturerMultiSelectProps {
  manufacturers: string[];
  selected: string[];
  onChange: (value: string[]) => void;
  hasEmptyManufacturers?: boolean;
}

export default function ManufacturerMultiSelect({
  manufacturers,
  selected,
  onChange,
  hasEmptyManufacturers = false,
}: ManufacturerMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // 검색 필터링된 제조사 목록
  const filteredManufacturers = manufacturers
    .filter((m) => m !== "")
    .filter((manufacturer) =>
      manufacturer.toLowerCase().includes(searchTerm.toLowerCase())
    );

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(target) &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
        setSearchTerm(""); // 드롭다운 닫힐 때 검색어 초기화
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right - 200 + window.scrollX,
      });
    }
  }, [open]);

  // 드롭다운이 열릴 때 검색 입력창에 포커스
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const toggle = (manufacturer: string) => {
    if (selected.includes(manufacturer)) {
      onChange(selected.filter((m) => m !== manufacturer));
    } else {
      onChange([...selected, manufacturer]);
    }
  };

  // 전체 선택
  const handleSelectAll = () => {
    const allOptions = hasEmptyManufacturers
      ? ["__empty__", ...filteredManufacturers]
      : filteredManufacturers;

    const allSelected = allOptions.every((option) => selected.includes(option));

    if (allSelected) {
      // 전체 선택 상태일 때 전체 취소
      onChange(selected.filter((item) => !allOptions.includes(item)));
    } else {
      // 전체 선택 상태가 아닐 때 현재 필터링된 옵션들을 모두 추가
      const newSelected = Array.from(new Set([...selected, ...allOptions]));
      onChange(newSelected);
    }
  };

  // esc 키 누르면 닫기
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearchTerm("");
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className="w-auto h-auto border-none bg-transparent p-1 hover:bg-white/10 rounded flex items-center justify-center"
        onClick={() => setOpen(!open)}
      >
        <ChevronDown className="w-4 h-4 text-white" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed p-0 w-[200px] bg-white shadow-lg rounded border"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: 100,
            }}
          >
            {/* 검색 입력 영역 */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="키워드 입력"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-dark-red"
                />
              </div>
            </div>

            {/* 전체 선택/해제 버튼 */}
            <div className="px-2 py-1 border-b border-gray-100">
              <button
                onClick={handleSelectAll}
                className="w-full text-left text-xs text-dark-red hover:bg-gray-50 py-1 px-1 rounded"
              >
                {(() => {
                  const allOptions = hasEmptyManufacturers
                    ? ["__empty__", ...filteredManufacturers]
                    : filteredManufacturers;
                  const allSelected = allOptions.every((option) =>
                    selected.includes(option)
                  );
                  return allSelected ? "전체 해제" : "전체 선택";
                })()}
              </button>
            </div>

            {/* 리스트 */}
            <div className="max-h-60 overflow-y-auto py-1">
              {/* Empty 옵션 - undefined/null 제조사가 있을 때만 표시 */}
              {hasEmptyManufacturers && (
                <div
                  className="flex items-center justify-between w-full py-1 px-2 cursor-pointer gap-2 hover:bg-gray-50"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle("__empty__");
                    return false;
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                >
                  <span className="text-xs text-gray-A font-normal truncate">
                    Empty
                  </span>
                  <span
                    className={cn(
                      "flex items-center justify-center w-4 h-4 border border-dark-red rounded-[2px] shrink-0",
                      selected.includes("__empty__")
                        ? "bg-dark-red"
                        : "bg-white"
                    )}
                  >
                    {selected.includes("__empty__") && (
                      <CheckSvg className="w-[10px] h-[7px]" />
                    )}
                  </span>
                </div>
              )}

              {filteredManufacturers.length === 0 && searchTerm ? (
                <div className="px-2 py-2 text-xs text-gray-500 text-center">
                  검색 결과가 없습니다.
                </div>
              ) : (
                filteredManufacturers.map((manufacturer) => {
                  const isChecked = selected.includes(manufacturer);
                  return (
                    <div
                      key={manufacturer}
                      className="flex items-center justify-between w-full py-1 px-2 cursor-pointer gap-2 hover:bg-gray-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggle(manufacturer);
                      }}
                    >
                      <span
                        className="text-xs text-gray-A font-normal truncate"
                        title={manufacturer}
                      >
                        {manufacturer}
                      </span>
                      <span
                        className={cn(
                          "flex items-center justify-center w-4 h-4 border border-dark-red rounded-[2px] shrink-0",
                          isChecked ? "bg-dark-red" : "bg-white"
                        )}
                      >
                        {isChecked && <CheckSvg className="w-[10px] h-[7px]" />}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
